import { createContext, createElement, useContext, useMemo } from 'react'

import { useConnectorStatusFormatter } from '../utils/connectorStatus'
import { useAssistantActionStatusFormatter } from './assistant'
import {
  useAgreementCategoryFormatter,
  useAgreementEntityTypeFormatter,
  useAgreementLanguageFormatter,
  useAgreementStatusFormatter,
  useAgreementTypeFormatter,
  useBillingCycleFormatter,
  useCollectionPolicyFormatter,
  useDemandChargeAllocationMethodFormatter,
  useEntityCountLevelFormatter,
  useLineItemGroupingFormatter,
  useMeterUnitFormatter,
  usePaymentOutcomeFormatter,
  usePaymentTerminalStatusFormatter,
  usePricingModelFormatter,
  useProductEntityTypeFormatter,
  useProductPricingTypeFormatter,
  useProductStatusFormatter,
  useProductTypeFormatter,
  useProductVisibilityFormatter,
  useReimbursementStatusFormatter,
  useTierQuantitySourceFormatter,
  useUsagePricingTypeFormatter,
  useVolumeTiersModeFormatter
} from './billing'
import {
  useAdaAccessibleFormatter,
  useCommissioningStateFormatter,
  useConnectorFormatFormatter,
  useConnectorNameFormatter,
  useConnectorPowerTypeFormatter,
  useConnectorTypeFormatter,
  useDiagnosticStatusFormatter,
  useFirmwareStatusFormatter,
  useInstallationTypeFormatter,
  useNetworkConnectionMethodFormatter
} from './cms'
import { useCurrencyFormatter } from './currency'
import { useCurrentFormatter } from './current'
import useUsageBucketFormatter from './dashboard'
import {
  useDateTimeFormatter,
  useDateTimeRangeFormatter,
  useDateTimeRelativeDayFormatter,
  useDateTimeRelativeFormatter
} from './datetime'
import useDaysOfWeekFormatter from './days'
import { useDistanceFormatter } from './distance'
import { useDurationFormatter } from './duration'
import { useEfficiencyFormatter } from './efficiency'
import { useEmissionsFormatter } from './emissions'
import {
  useBatteryStatusFormatter,
  useBessLiveStatusFormatter,
  useBessStatusBucketFormatter,
  useCommandModeFormatter,
  useComponentEntityTypeFormatter,
  useConnectivityStatusFormatter,
  useControlModeFormatter,
  useDeviceTypeFormatter,
  useGeneratorStatusFormatter,
  useGridModeFormatter,
  useInfeasibilityReasonFormatter,
  useLoadManagementFormatter,
  useMeterEntityTypeFormatter,
  useMeterTypeFormatter,
  useModelTypeFormatter,
  useOcppOutboundFilterFormatter,
  useOcppProxyModeFormatter,
  useOfflineControlsFormatter,
  useRegulationTypeFormatter,
  useRunStateFormatter,
  useSolarArrayStatusFormatter
} from './ems'
import { useEnergyFormatter } from './energy'
import {
  useDeploymentStatusFormatter,
  useFailureHandlingFormatter,
  useRolloutStrategyFormatter,
  useStationStatusFormatter
} from './firmwareDeployments'
import { useFuelFormatter, useFuelTypeFormatter, useTankShapeFormatter } from './fuel'
import useLoadCategoryFormatter from './load'
import { useNumberFormatter } from './number'
import {
  usePlanStatusFormatter,
  useProjectStatusFormatter,
  useTicketPriorityFormatter,
  useTicketStatusFormatter
} from './operations'
import { usePercentFormatter } from './percent'
import { usePowerFormatter } from './power'
import { useFacilityFormatter, useParkingTypeFormatter } from './site'
import useSiteAssetTypeFormatter from './siteAssetType'
import { useSpeedFormatter } from './speed'
import { useSocFormatter } from './stateOfCharge'
import { useTemperatureFormatter } from './temperature'
import { useRecurrenceFormatter } from './time'
import {
  useAuthTagEntityTypeFormatter,
  useAuthTagTypeFormatter,
  useTransactionRateTypeFormatter,
  useTransactionStopActorFormatter,
  useTransactionStopReasonFormatter
} from './transaction'
import { useUnitsFormatter } from './units'
import { useAccessLevelFormatter, useUserRoleFormatter } from './user'
import {
  useChargeFallbackLevelFormatter,
  useChargeModelStatusFormatter,
  useChargerClassFormatter,
  useDriverTypeFormatter,
  useEfficiencyBaselineSourceFormatter,
  useEfficiencyFallbackLevelFormatter,
  useEfficiencyModelStatusFormatter,
  usePowerCurveRegionSourceFormatter,
  useVehicleCommissionStatusFormatter,
  useVehicleStatusFormatter
} from './vehicle'
import { useVoltageFormatter } from './voltage'
import { useWeightFormatter } from './weight'
import useWorkflowsFormatters from './workflows'

/**
 * Build and memoize the unified set of formatter functions used across the app.
 *
 * Notes
 * - This function calls React hooks (e.g., next-intl formatters), so it must only
 *   be invoked from within a React render (it is used by `FormattingProvider`).
 * - Consumers should access formatters via `useFormatters()`; do not call this
 *   function directly from components.
 * - Provider placement: `FormattingProvider` must render under `NextIntlClientProvider`
 *   (and any user-pref providers) because many formatters depend on locale and
 *   user preferences (time zone, units, etc.).
 * - Maintenance: When adding a new formatter hook, instantiate it here and include
 *   it in the returned object and dependency array in alphabetical order to keep
 *   diffs readable and predictable.
 *
 * Performance
 * - The return value is memoized to provide a stable object identity across rerenders
 *   as long as dependencies do not change.
 */
function useBuildFormatters() {
  const adaAccessible = useAdaAccessibleFormatter()
  const agreementCategory = useAgreementCategoryFormatter()
  const agreementEntityType = useAgreementEntityTypeFormatter()
  const agreementLanguage = useAgreementLanguageFormatter()
  const agreementStatus = useAgreementStatusFormatter()
  const agreementType = useAgreementTypeFormatter()
  const assistantActionStatus = useAssistantActionStatusFormatter()
  const authTagEntityType = useAuthTagEntityTypeFormatter()
  const authTagType = useAuthTagTypeFormatter()
  const batteryStatus = useBatteryStatusFormatter()
  const bessLiveStatus = useBessLiveStatusFormatter()
  const bessStatusBucket = useBessStatusBucketFormatter()
  const billingCycle = useBillingCycleFormatter()
  const collectionPolicy = useCollectionPolicyFormatter()
  const commandMode = useCommandModeFormatter()
  const demandChargeAllocationMethod = useDemandChargeAllocationMethodFormatter()
  const commissioningState = useCommissioningStateFormatter()
  const connectivityStatus = useConnectivityStatusFormatter()
  const connectorName = useConnectorNameFormatter()
  const connectorFormat = useConnectorFormatFormatter()
  const connectorPowerType = useConnectorPowerTypeFormatter()
  const connectorStatus = useConnectorStatusFormatter()
  const connectorType = useConnectorTypeFormatter()
  const controlMode = useControlModeFormatter()
  const currency = useCurrencyFormatter()
  const current = useCurrentFormatter()
  const dateTime = useDateTimeFormatter()
  const dateTimeRange = useDateTimeRangeFormatter()
  const daysOfWeek = useDaysOfWeekFormatter()
  const componentEntityType = useComponentEntityTypeFormatter()
  const deviceType = useDeviceTypeFormatter()
  const diagnosticStatus = useDiagnosticStatusFormatter()
  const distance = useDistanceFormatter()
  const driverType = useDriverTypeFormatter()
  const chargeFallbackLevel = useChargeFallbackLevelFormatter()
  const chargeModelStatus = useChargeModelStatusFormatter()
  const chargerClass = useChargerClassFormatter()
  const powerCurveRegionSource = usePowerCurveRegionSourceFormatter()
  const vehicleCommissionStatus = useVehicleCommissionStatusFormatter()
  const duration = useDurationFormatter()
  const efficiency = useEfficiencyFormatter()
  const efficiencyBaselineSource = useEfficiencyBaselineSourceFormatter()
  const efficiencyFallbackLevel = useEfficiencyFallbackLevelFormatter()
  const efficiencyModelStatus = useEfficiencyModelStatusFormatter()
  const emissions = useEmissionsFormatter()
  const energy = useEnergyFormatter()
  const facility = useFacilityFormatter()
  const firmwareDeploymentStatus = useDeploymentStatusFormatter()
  const firmwareFailureHandling = useFailureHandlingFormatter()
  const firmwareRolloutStrategy = useRolloutStrategyFormatter()
  const firmwareStationStatus = useStationStatusFormatter()
  const firmwareStatus = useFirmwareStatusFormatter()
  const fuel = useFuelFormatter()
  const fuelType = useFuelTypeFormatter()
  const tankShape = useTankShapeFormatter()
  const generatorStatus = useGeneratorStatusFormatter()
  const gridMode = useGridModeFormatter()
  const infeasibilityReason = useInfeasibilityReasonFormatter()
  const installationType = useInstallationTypeFormatter()
  const lineItemGrouping = useLineItemGroupingFormatter()
  const loadCategory = useLoadCategoryFormatter()
  const loadManagement = useLoadManagementFormatter()
  const meterEntityType = useMeterEntityTypeFormatter()
  const meterType = useMeterTypeFormatter()
  const meterUnit = useMeterUnitFormatter()
  const modelType = useModelTypeFormatter()
  const networkConnectionMethod = useNetworkConnectionMethodFormatter()
  const number = useNumberFormatter()
  const ocppOutboundFilter = useOcppOutboundFilterFormatter()
  const ocppProxyMode = useOcppProxyModeFormatter()
  const offlineControls = useOfflineControlsFormatter()
  const operationsPlanStatus = usePlanStatusFormatter()
  const operationsProjectStatus = useProjectStatusFormatter()
  const operationsTicketPriority = useTicketPriorityFormatter()
  const operationsTicketStatus = useTicketStatusFormatter()
  const parkingType = useParkingTypeFormatter()
  const paymentOutcome = usePaymentOutcomeFormatter()
  const paymentTerminalStatus = usePaymentTerminalStatusFormatter()
  const percent = usePercentFormatter()
  const power = usePowerFormatter()
  const pricingModel = usePricingModelFormatter()
  const productEntityType = useProductEntityTypeFormatter()
  const productPricingType = useProductPricingTypeFormatter()
  const productStatus = useProductStatusFormatter()
  const productType = useProductTypeFormatter()
  const productVisibility = useProductVisibilityFormatter()
  const recurrence = useRecurrenceFormatter()
  const dateTimeRelative = useDateTimeRelativeFormatter()
  const dateTimeRelativeDay = useDateTimeRelativeDayFormatter()
  const entityCountLevel = useEntityCountLevelFormatter()
  const tierQuantitySource = useTierQuantitySourceFormatter()
  const volumeTiersMode = useVolumeTiersModeFormatter()
  const reimbursementStatus = useReimbursementStatusFormatter()
  const regulationType = useRegulationTypeFormatter()
  const runState = useRunStateFormatter()
  const soc = useSocFormatter()
  const solarArrayStatus = useSolarArrayStatusFormatter()
  const speed = useSpeedFormatter()
  const temperature = useTemperatureFormatter()
  const transactionRateType = useTransactionRateTypeFormatter()
  const transactionStopActor = useTransactionStopActorFormatter()
  const transactionStopReason = useTransactionStopReasonFormatter()
  const units = useUnitsFormatter()
  const usageBucket = useUsageBucketFormatter()
  const usagePricingType = useUsagePricingTypeFormatter()
  const vehicleStatus = useVehicleStatusFormatter()
  const voltage = useVoltageFormatter()
  const weight = useWeightFormatter()
  const workflows = useWorkflowsFormatters()
  const userRole = useUserRoleFormatter()
  const accessLevel = useAccessLevelFormatter()
  const siteAssetType = useSiteAssetTypeFormatter()

  return useMemo(
    () => ({
      adaAccessible,
      assistantActionStatus,
      batteryStatus,
      bessLiveStatus,
      bessStatusBucket,
      billing: {
        agreementCategory,
        agreementEntityType,
        agreementLanguage,
        agreementStatus,
        agreementType,
        billingCycle,
        collectionPolicy,
        demandChargeAllocationMethod,
        entityCountLevel,
        lineItemGrouping,
        paymentOutcome,
        paymentTerminalStatus,
        productEntityType,
        pricingModel,
        productPricingType,
        productStatus,
        productType,
        productVisibility,
        reimbursementStatus,
        tierQuantitySource,
        usagePricingType,
        volumeTiersMode
      },
      commandMode,
      commissioningState,
      connectivityStatus,
      connectorName,
      connectorFormat,
      connectorPowerType,
      connectorStatus,
      connectorType,
      controlMode,
      currency,
      current,
      dateTime,
      dateTimeRange,
      dateTimeRelative,
      dateTimeRelativeDay,
      daysOfWeek,
      componentEntityType,
      deviceType,
      diagnosticStatus,
      distance,
      driverType,
      duration,
      efficiency,
      efficiencyBaselineSource,
      efficiencyFallbackLevel,
      efficiencyModelStatus,
      emissions,
      energy,
      fuel,
      fuelType,
      tankShape,
      firmwareDeployment: {
        failureHandling: firmwareFailureHandling,
        stationStatus: firmwareStationStatus,
        status: firmwareDeploymentStatus,
        strategy: firmwareRolloutStrategy
      },
      firmwareStatus,
      generatorStatus,
      gridMode,
      infeasibilityReason,
      installationType,
      loadCategory,
      loadManagement,
      meterEntityType,
      meterType,
      meterUnit,
      modelType,
      networkConnectionMethod,
      number,
      ocppOutboundFilter,
      ocppProxyMode,
      offlineControls,
      operations: {
        planStatus: operationsPlanStatus,
        projectStatus: operationsProjectStatus,
        ticketPriority: operationsTicketPriority,
        ticketStatus: operationsTicketStatus
      },
      percent,
      power,
      recurrence,
      regulationType,
      runState,
      site: {
        facility,
        parkingType
      },
      soc,
      solarArrayStatus,
      speed,
      temperature,
      transaction: {
        authTagEntityType,
        authTagType,
        rateType: transactionRateType,
        stopActor: transactionStopActor,
        stopReason: transactionStopReason
      },
      units,
      usageBucket,
      vehicleStatus,
      vehicleCommissionStatus,
      chargeFallbackLevel,
      chargeModelStatus,
      chargerClass,
      powerCurveRegionSource,
      voltage,
      weight,
      workflows,
      siteAssetType,
      userRole,
      accessLevel
    }),
    [
      adaAccessible,
      agreementCategory,
      agreementEntityType,
      agreementLanguage,
      agreementStatus,
      agreementType,
      assistantActionStatus,
      authTagEntityType,
      authTagType,
      batteryStatus,
      bessLiveStatus,
      bessStatusBucket,
      billingCycle,
      collectionPolicy,
      demandChargeAllocationMethod,
      commandMode,
      commissioningState,
      connectivityStatus,
      connectorName,
      connectorFormat,
      connectorPowerType,
      connectorStatus,
      connectorType,
      controlMode,
      currency,
      current,
      dateTime,
      dateTimeRange,
      dateTimeRelative,
      dateTimeRelativeDay,
      daysOfWeek,
      componentEntityType,
      deviceType,
      diagnosticStatus,
      distance,
      driverType,
      duration,
      efficiency,
      efficiencyBaselineSource,
      efficiencyFallbackLevel,
      efficiencyModelStatus,
      emissions,
      energy,
      entityCountLevel,
      facility,
      firmwareDeploymentStatus,
      firmwareFailureHandling,
      firmwareRolloutStrategy,
      firmwareStationStatus,
      firmwareStatus,
      fuel,
      fuelType,
      tankShape,
      generatorStatus,
      gridMode,
      infeasibilityReason,
      installationType,
      lineItemGrouping,
      loadCategory,
      loadManagement,
      meterEntityType,
      meterType,
      meterUnit,
      modelType,
      networkConnectionMethod,
      number,
      ocppOutboundFilter,
      ocppProxyMode,
      offlineControls,
      operationsPlanStatus,
      operationsProjectStatus,
      operationsTicketPriority,
      operationsTicketStatus,
      parkingType,
      paymentOutcome,
      paymentTerminalStatus,
      percent,
      power,
      pricingModel,
      productEntityType,
      productPricingType,
      productStatus,
      productType,
      productVisibility,
      recurrence,
      regulationType,
      reimbursementStatus,
      runState,
      tierQuantitySource,
      soc,
      solarArrayStatus,
      speed,
      temperature,
      transactionRateType,
      transactionStopActor,
      transactionStopReason,
      units,
      usageBucket,
      usagePricingType,
      vehicleStatus,
      vehicleCommissionStatus,
      chargeFallbackLevel,
      chargeModelStatus,
      chargerClass,
      powerCurveRegionSource,
      volumeTiersMode,
      voltage,
      weight,
      workflows,
      siteAssetType,
      userRole,
      accessLevel
    ]
  )
}

export type Formatters = ReturnType<typeof useBuildFormatters>

const FormattingContext = createContext<Formatters | null>(null)

export function FormattingProvider({ children }: { children: React.ReactNode }) {
  const value = useBuildFormatters()
  return createElement(FormattingContext.Provider, { value }, children)
}

/**
 * Access the global, memoized set of formatter functions.
 *
 * Usage
 * - Call inside React function components or hooks: `const format = useFormatters()`.
 *
 * Requirements
 * - Must be rendered under `FormattingProvider`, which itself must be under
 *   `NextIntlClientProvider` so locale-aware formatters are initialized correctly.
 *
 * Behavior
 * - Throws if used outside of a `FormattingProvider` to avoid silently constructing
 *   duplicate formatter instances.
 */
export default function useFormatters() {
  const ctx = useContext(FormattingContext)
  if (ctx) return ctx
  throw new Error('useFormatters must be used within a FormattingProvider')
}
